import React, { useEffect, useMemo, useState } from "react";
import { useGetConfirmedQuery } from "../../redux/slices/ConfirmedApiSlice";
import { setConfirmed } from "../../redux/slices/confirmedSlice";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaPhone,
  FaReceipt,
  FaSearch,
  FaShoppingBag,
  FaSpinner,
  FaTimes,
  FaTruck,
  FaUser,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";

/* AZƏRBAYCAN AYLARI */
const AZ_MONTHS = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"
];

/* TARİX FORMATLARI */
const formatDate = (value) => {
  if (!value) return "Tarix yoxdur";
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    const bakuTime = new Date(date.getTime() + 4 * 60 * 60 * 1000);
    const day = bakuTime.getUTCDate();
    const month = AZ_MONTHS[bakuTime.getUTCMonth()];
    const year = bakuTime.getUTCFullYear();
    const hour = String(bakuTime.getUTCHours()).padStart(2, "0");
    const minute = String(bakuTime.getUTCMinutes()).padStart(2, "0");

    return `${day} ${month} ${year}, ${hour}:${minute}`;
  } catch (error) {
    return String(value);
  }
};

const formatShortDate = (value) => {
  if (!value) return "—";
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    const bakuTime = new Date(date.getTime() + 4 * 60 * 60 * 1000);
    const day = String(bakuTime.getUTCDate()).padStart(2, "0");
    const month = String(bakuTime.getUTCMonth() + 1).padStart(2, "0");
    const year = bakuTime.getUTCFullYear();
    const hour = String(bakuTime.getUTCHours()).padStart(2, "0");
    const minute = String(bakuTime.getUTCMinutes()).padStart(2, "0");

    return `${day}.${month}.${year} ${hour}:${minute}`;
  } catch (error) {
    return "—";
  }
};

/* MƏHSUL ŞƏKİLLƏRİ HƏLLEDİCİSİ */
const getProductImages = (product) => {
  if (!product?.photo) return [];
  if (Array.isArray(product.photo)) return product.photo.filter(Boolean);
  if (typeof product.photo === "string") return [product.photo];
  return [];
};

/* PRODUCT IMAGE CAROUSEL */
const ProductImageCarousel = ({ images, title, size = "normal" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const hasMultiple = safeImages.length > 1;

  useEffect(() => {
    setCurrentIndex(0);
  }, [safeImages.length]);

  useEffect(() => {
    if (!hasMultiple) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= safeImages.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [hasMultiple, safeImages.length]);

  const nextImage = (e) => {
    e?.stopPropagation();
    if (!hasMultiple) return;
    setCurrentIndex((prev) => (prev >= safeImages.length - 1 ? 0 : prev + 1));
  };

  const previousImage = (e) => {
    e?.stopPropagation();
    if (!hasMultiple) return;
    setCurrentIndex((prev) => (prev <= 0 ? safeImages.length - 1 : prev - 1));
  };

  const handleTouchStart = (e) => {
    if (!hasMultiple) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches?.[0]?.clientX ?? null);
  };

  const handleTouchMove = (e) => {
    if (!hasMultiple) return;
    setTouchEnd(e.targetTouches?.[0]?.clientX ?? null);
  };

  const handleTouchEnd = () => {
    if (!hasMultiple || touchStart === null || touchEnd === null) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) >= 35) {
      if (distance > 0) nextImage();
      else previousImage();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (safeImages.length === 0) {
    return (
      <div className={`
        ${size === "small" ? "w-16 h-16 sm:w-20 sm:h-20" : "w-20 h-20 sm:w-24 sm:h-24"}
        rounded-xl bg-slate-100 dark:bg-slate-800/60 flex items-center justify-center shrink-0 border border-slate-200/60 dark:border-white/5
      `}>
        <FaBoxOpen size={size === "small" ? 18 : 22} className="text-slate-400" />
      </div>
    );
  }

  const containerClasses = size === "small"
    ? "w-16 h-16 sm:w-20 sm:h-20 rounded-xl"
    : "w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-2xl";

  return (
    <div
      className={`
        relative ${containerClasses} shrink-0 overflow-hidden 
        bg-slate-100 dark:bg-slate-900 border border-slate-200/80 
        dark:border-white/10 select-none touch-pan-y group
      `}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={safeImages[currentIndex]}
        alt={title || `Şəkil ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-opacity duration-300"
        draggable={false}
      />

      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={previousImage}
            aria-label="Əvvəlki şəkil"
            className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <FaChevronLeft size={8} />
          </button>
          <button
            type="button"
            onClick={nextImage}
            aria-label="Növbəti şəkil"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <FaChevronRight size={8} />
          </button>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-black/70 text-white text-[9px] font-bold">
            {currentIndex + 1}/{safeImages.length}
          </div>
        </>
      )}
    </div>
  );
};

/* MAIN COMPONENT */
const Confirmed = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isError, error: confirmedError, refetch } = useGetConfirmedQuery();

  const [userPayments, setUserPayments] = useState({});
  const [paymentLoading, setPaymentLoading] = useState({});
  const [paymentErrors, setPaymentErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [openPoct, setOpenPoct] = useState(null);
  const [selectedOrderKey, setSelectedOrderKey] = useState(null);
  const [generalError, setGeneralError] = useState("");

  const orders = Array.isArray(data) ? data : [];

  const getErrorMessage = (error) => {
    const serverMessage = error?.response?.data?.message || error?.response?.data?.error || error?.data?.message || error?.data?.error;
    if (serverMessage) return serverMessage;
    switch (error?.response?.status) {
      case 400: return "Göndərilən məlumatlar düzgün deyil.";
      case 401: return "Sessiyanız başa çatıb. Yenidən daxil olun.";
      case 403: return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";
      case 404: return "Tələb olunan məlumat tapılmadı.";
      case 429: return "Çox sayda sorğu göndərildi. Bir qədər gözləyin.";
      default: break;
    }
    if (error?.response?.status >= 500) return "Serverdə texniki problem baş verdi.";
    if (error?.code === "ERR_NETWORK" || error?.message?.toLowerCase()?.includes("network")) {
      return "Serverə qoşulmaq mümkün olmadı.";
    }
    return error?.message || "Naməlum xəta baş verdi. Yenidən cəhd edin.";
  };

  useEffect(() => {
    if (!data) return;
    try {
      dispatch(setConfirmed(data));
    } catch (err) {
      console.error("Redux xətası:", err);
    }

    setGeneralError("");
    setPaymentErrors({});

    orders.forEach((cart) => {
      const cartId = cart?._id;
      if (!cartId) return;

      setPaymentLoading((prev) => ({ ...prev, [cartId]: true }));

      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/paymentId/${cartId}`, { withCredentials: true })
        .then((res) => {
          const resData = res?.data;
          const hasData = resData && ((Array.isArray(resData) && resData.length > 0) || (!Array.isArray(resData) && Object.keys(resData).length > 0));
          if (hasData) {
            setUserPayments((prev) => ({ ...prev, [cartId]: resData }));
          } else {
            setPaymentErrors((prev) => ({ ...prev, [cartId]: "Bu sifariş üçün ödəniş məlumatı tapılmadı." }));
          }
        })
        .catch((err) => {
          setPaymentErrors((prev) => ({ ...prev, [cartId]: getErrorMessage(err) }));
        })
        .finally(() => {
          setPaymentLoading((prev) => ({ ...prev, [cartId]: false }));
        });
    });
  }, [data, dispatch]);

  useEffect(() => {
    if (isError) {
      setGeneralError(getErrorMessage(confirmedError));
    }
  }, [isError, confirmedError]);

  const filteredOrders = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return orders;
    return orders.filter((order) =>
      order?.orderCode?.toString().toLowerCase().includes(search)
    );
  }, [orders, searchTerm]);

  const stats = useMemo(() => {
    let products = 0, paid = 0, pending = 0, failed = 0;
    orders.forEach((order) => {
      const orderProducts = Array.isArray(order?.products) ? order.products : [];
      products += orderProducts.reduce((sum, p) => sum + (Number(p?.quantity) || 0), 0);
      if (order?.paymentStatus === "paid") paid++;
      if (order?.paymentStatus === "pending") pending++;
      if (order?.paymentStatus === "failed") failed++;
    });
    return { orders: orders.length, products, paid, pending, failed };
  }, [orders]);

  const handleRetry = async () => {
    setGeneralError("");
    setUserPayments({});
    setPaymentErrors({});
    setPaymentLoading({});
    try {
      await refetch();
    } catch (err) {
      setGeneralError(getErrorMessage(err));
    }
  };

  const getOrderData = (order) => {
    const rawPayment = userPayments[order?._id];
    const paymentArray = Array.isArray(rawPayment) ? rawPayment : rawPayment ? [rawPayment] : [];
    const products = Array.isArray(order?.products) ? order.products : [];

    const productTotal = products.reduce((sum, p) => {
      return sum + (Number(p?.price) || 0) * (Number(p?.quantity) || 0);
    }, 0);

    let poctFee = 0;
    const poct = paymentArray?.[0]?.poct;
    if (poct) {
      const match = poct.match(/(\d+(?:\.\d+)?)\s*\*AZN/);
      if (match) poctFee = parseFloat(match[1]);
    }

    return { products, paymentArray, productTotal, poctFee, total: productTotal + poctFee };
  };

  const getStatus = (status) => {
    if (status === "paid") {
      return {
        label: "Təsdiqlənib",
        color: "text-emerald-600 dark:text-emerald-400",
        bg: "bg-emerald-50 dark:bg-emerald-500/10",
        border: "border-emerald-200 dark:border-emerald-500/20",
        icon: <FaCheckCircle />,
      };
    }
    if (status === "pending") {
      return {
        label: "Gözlənilir",
        color: "text-amber-600 dark:text-amber-400",
        bg: "bg-amber-50 dark:bg-amber-500/10",
        border: "border-amber-200 dark:border-amber-500/20",
        icon: <FaClock />,
      };
    }
    return {
      label: "Ləğv edilib",
      color: "text-rose-600 dark:text-rose-400",
      bg: "bg-rose-50 dark:bg-rose-500/10",
      border: "border-rose-200 dark:border-rose-500/20",
      icon: <FaTimes />,
    };
  };

  const selectedOrder = useMemo(() => {
    if (!selectedOrderKey) return null;
    return filteredOrders.find((order, index) => {
      const key = order?._id || order?.orderCode || `order-${index}`;
      return key === selectedOrderKey;
    }) || null;
  }, [filteredOrders, selectedOrderKey]);

  const selectedOrderData = selectedOrder ? getOrderData(selectedOrder) : null;
  const selectedStatus = selectedOrder ? getStatus(selectedOrder.paymentStatus) : null;

  const selectOrder = (order, index) => {
    const key = order?._id || order?.orderCode || `order-${index}`;
    setSelectedOrderKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#070a0f] text-slate-800 dark:text-slate-100 antialiased pb-12">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8 pt-4 sm:pt-8">
        
        {/* HEADER */}
        <header className="mb-6 sm:mb-8">
          <div className="rounded-2xl sm:rounded-3xl border border-slate-200/80 bg-white p-4 sm:p-6 lg:p-8 shadow-sm dark:border-white/10 dark:bg-[#0c1017]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    Sifarişlərim
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                  Təsdiqlənmiş <span className="text-slate-400 dark:text-slate-500">Sifarişlər</span>
                </h1>
                <p className="mt-1.5 text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                  Sifarişlərinizi, ödəniş və çatdırılma detallarınızı bir yerdən rahatlıqla izləyin.
                </p>
              </div>

              {!isLoading && !generalError && (
                <div className="grid grid-cols-3 rounded-xl sm:rounded-2xl border border-slate-100 bg-slate-50/80 dark:border-white/5 dark:bg-[#080b11] divide-x divide-slate-200/60 dark:divide-white/5">
                  <HeaderStat value={stats.orders} label="Sifariş" />
                  <HeaderStat value={stats.products} label="Məhsul" />
                  <HeaderStat value={stats.paid} label="Təsdiq" green />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* SEARCH BAR */}
        {!isLoading && !generalError && (
          <div className="mb-6 rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-[#0c1017]">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Sifariş kodu ilə axtarın..."
                  className="h-11 w-full rounded-xl bg-slate-50 pl-10 pr-9 text-xs sm:text-sm font-medium outline-none transition placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 dark:bg-[#080b11] dark:focus:bg-[#0e131d]"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </div>

              <div className="hidden sm:flex h-11 items-center gap-2 rounded-xl bg-slate-50 px-4 text-xs font-bold text-slate-500 dark:bg-[#080b11] dark:text-slate-400">
                <FaShoppingBag size={12} />
                {filteredOrders.length} nəticə
              </div>
            </div>
          </div>
        )}

        {/* GENERAL ERROR */}
        {generalError && (
          <div className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/20 dark:bg-[#0c1017] mb-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500 dark:bg-rose-500/10">
                <FaExclamationTriangle size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Xəta baş verdi</h3>
                <p className="mt-0.5 text-xs sm:text-sm text-slate-500 dark:text-slate-400">{generalError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-4 rounded-xl bg-slate-900 px-5 py-2.5 text-xs font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900"
            >
              Yenidən yoxla
            </button>
          </div>
        )}

        {/* LOADING SKELETON */}
        {isLoading && (
          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7 xl:col-span-8 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-44 animate-pulse rounded-2xl border border-slate-200/60 bg-white dark:border-white/5 dark:bg-[#0c1017]" />
              ))}
            </div>
            <div className="hidden lg:block lg:col-span-5 xl:col-span-4 h-96 animate-pulse rounded-2xl border border-slate-200/60 bg-white dark:border-white/5 dark:bg-[#0c1017]" />
          </div>
        )}

        {/* EMPTY STATE */}
        {!isLoading && !generalError && filteredOrders.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-white/10 dark:bg-[#0c1017]">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-white/5">
              <FaBoxOpen size={24} />
            </div>
            <h3 className="mt-4 text-base font-bold">Sifariş tapılmadı</h3>
            <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Axtarış şərtinə uyğun təsdiqlənmiş sifariş yoxdur.
            </p>
          </div>
        )}

        {/* MAIN LIST & DETAILS LAYOUT */}
        {!isLoading && !generalError && filteredOrders.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-12 items-start">
            
            {/* ORDER LIST */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-4">
              {filteredOrders.map((order, index) => {
                const orderKey = order?._id || order?.orderCode || `order-${index}`;
                const isSelected = selectedOrderKey === orderKey;
                const status = getStatus(order?.paymentStatus);
                const { products, paymentArray, productTotal, poctFee, total } = getOrderData(order);

                const quantity = products.reduce((sum, p) => sum + (Number(p?.quantity) || 0), 0);
                const firstProduct = products[0];
                const images = getProductImages(firstProduct);

                const orderDate = order?.confirmedAt;
                const confirmationDate = order?.paymentStatus === "paid" ? order?.updatedAt : null;

                return (
                  <div
                    key={orderKey}
                    className={`
                      rounded-2xl border bg-white shadow-sm transition-all duration-200 
                      dark:bg-[#0c1017] overflow-hidden
                      ${isSelected
                        ? "border-blue-500 ring-2 ring-blue-500/10 dark:border-blue-500"
                        : "border-slate-200/80 hover:border-slate-300 dark:border-white/10"
                      }
                    `}
                  >
                    {/* CARD HEADER */}
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/50 px-4 py-3 dark:border-white/5 dark:bg-white/[0.02]">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${status.bg} ${status.color}`}>
                          {status.icon}
                        </span>
                        <div className="min-w-0">
                          <h3 className="truncate text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                            {order?.orderCode || "Sifariş"}
                          </h3>
                          <p className="text-[11px] text-slate-400">
                            {formatShortDate(orderDate)} • {quantity} məhsul
                          </p>
                        </div>
                      </div>

                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${status.bg} ${status.border} ${status.color}`}>
                        {status.label}
                      </span>
                    </div>

                    {/* CARD BODY */}
                    <div className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <ProductImageCarousel images={images} title={firstProduct?.title} size="small" />

                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 line-clamp-1">
                            {firstProduct?.title || "Məhsul"}
                          </h4>

                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">
                              {confirmationDate ? `Təsdiq: ${formatShortDate(confirmationDate)}` : `Tarix: ${formatShortDate(orderDate)}`}
                            </span>
                            {poctFee > 0 && (
                              <span className="rounded-lg bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">
                                Çatdırılma: {poctFee.toFixed(2)} AZN
                              </span>
                            )}
                          </div>
                        </div>

                        {/* PRICE & TOGGLE */}
                        <div className="flex items-center justify-between sm:flex-col sm:items-end border-t sm:border-0 pt-3 sm:pt-0 border-slate-100 dark:border-white/5">
                          <div className="text-left sm:text-right">
                            <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Yekun Məbləğ</p>
                            <p className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white">
                              {total.toFixed(2)} <span className="text-xs text-slate-400 font-normal">AZN</span>
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => selectOrder(order, index)}
                            className="mt-1 flex items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-2 text-xs font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900"
                          >
                            <FaReceipt size={11} />
                            {isSelected ? "Gizlət" : "Detallar"}
                          </button>
                        </div>
                      </div>

                      {/* EXTRA PRODUCTS BADGE */}
                      {products.length > 1 && (
                        <p className="mt-3 text-[11px] font-medium text-slate-400 border-t border-slate-100 dark:border-white/5 pt-2">
                          + {products.length - 1} digər məhsul mövcuddur
                        </p>
                      )}
                    </div>

                    {/* MOBILE EXPANDED DETAILS INLINE */}
                    {isSelected && (
                      <div className="block lg:hidden border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#080b11] p-4">
                        <OrderDetails
                          order={order}
                          products={products}
                          paymentArray={paymentArray}
                          productTotal={productTotal}
                          poctFee={poctFee}
                          total={total}
                          status={status}
                          paymentLoading={Boolean(order?._id && paymentLoading[order._id])}
                          paymentError={order?._id ? paymentErrors[order._id] : null}
                          onOpenPoct={setOpenPoct}
                          getProductImages={getProductImages}
                          orderDate={orderDate}
                          confirmationDate={confirmationDate}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* DESKTOP STICKY DETAIL PANEL */}
            <div className="hidden lg:block lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6">
              {selectedOrder && selectedOrderData && selectedStatus ? (
                <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-white/10 dark:bg-[#0c1017] overflow-hidden">
                  <div className="border-b border-slate-100 p-4 dark:border-white/5 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Detallı Baxış</span>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        {selectedOrder.orderCode || "Sifariş"}
                      </h3>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold ${selectedStatus.bg} ${selectedStatus.border} ${selectedStatus.color}`}>
                      {selectedStatus.label}
                    </span>
                  </div>

                  <OrderDetails
                    order={selectedOrder}
                    products={selectedOrderData.products}
                    paymentArray={selectedOrderData.paymentArray}
                    productTotal={selectedOrderData.productTotal}
                    poctFee={selectedOrderData.poctFee}
                    total={selectedOrderData.total}
                    status={selectedStatus}
                    paymentLoading={Boolean(selectedOrder?._id && paymentLoading[selectedOrder._id])}
                    paymentError={selectedOrder?._id ? paymentErrors[selectedOrder._id] : null}
                    onOpenPoct={setOpenPoct}
                    getProductImages={getProductImages}
                    orderDate={selectedOrder?.confirmedAt}
                    confirmationDate={selectedOrder?.paymentStatus === "paid" ? selectedOrder?.updatedAt : null}
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-[#0c1017]">
                  <FaReceipt className="mx-auto text-slate-300 dark:text-slate-600 mb-2" size={28} />
                  <p className="text-xs font-medium text-slate-400">
                    Sifarişin ətraflı məlumatına baxmaq üçün siyahıdan bir sifariş seçin.
                  </p>
                </div>
              )}
            </div>

          </div>
        )}

        {/* POCT MODAL */}
        {openPoct && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => setOpenPoct(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-white/10 dark:bg-[#0c1017]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <FaTruck className="text-blue-500" size={16} />
                  <h3 className="text-sm font-bold">Poçt Məlumatı</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setOpenPoct(null)}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              <div className="py-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap max-h-60 overflow-y-auto">
                {openPoct}
              </div>

              <button
                type="button"
                onClick={() => setOpenPoct(null)}
                className="w-full rounded-xl bg-slate-900 py-2.5 text-xs font-bold text-white dark:bg-white dark:text-slate-900"
              >
                Bağla
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* HEADER STAT HELPERS */
const HeaderStat = ({ value, label, green }) => (
  <div className="p-3 text-center">
    <p className={`text-base sm:text-xl font-black ${green ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
      {value}
    </p>
    <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mt-0.5">{label}</p>
  </div>
);

/* ORDER DETAILS COMPONENT */
const OrderDetails = ({
  order,
  products,
  paymentArray = [],
  productTotal,
  poctFee,
  total,
  status,
  paymentLoading,
  paymentError,
  onOpenPoct,
  getProductImages,
  orderDate,
  confirmationDate,
}) => {
  return (
    <div className="p-4 space-y-4">
      {/* TARİXLƏR */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 p-2.5 dark:border-white/5 dark:bg-white/[0.02]">
          <FaCalendarAlt className="text-slate-400 shrink-0" size={12} />
          <div className="min-w-0">
            <p className="text-[9px] uppercase font-bold text-slate-400">Sifariş Tarixi</p>
            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{formatDate(orderDate)}</p>
          </div>
        </div>

        {confirmationDate && (
          <div className="flex items-center gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50/50 p-2.5 dark:border-emerald-500/10 dark:bg-emerald-500/[0.03]">
            <FaCheckCircle className="text-emerald-500 shrink-0" size={12} />
            <div className="min-w-0">
              <p className="text-[9px] uppercase font-bold text-emerald-600/80 dark:text-emerald-400/80">Təsdiqlənmə</p>
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 truncate">{formatDate(confirmationDate)}</p>
            </div>
          </div>
        )}
      </div>

      {/* PAYMENT STATUS STATES */}
      {paymentLoading && (
        <div className="flex items-center gap-2 rounded-xl bg-blue-50 p-3 text-xs font-semibold text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">
          <FaSpinner className="animate-spin" size={12} />
          Ödəniş məlumatları yüklənir...
        </div>
      )}

      {paymentError && (
        <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
          <FaExclamationTriangle size={12} />
          {paymentError}
        </div>
      )}

      {/* MƏHSULLAR SİYAHISI */}
      <div>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
          Məhsullar ({products?.length || 0})
        </h4>
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
          {products?.map((p, idx) => {
            const price = Number(p?.price) || 0;
            const quantity = Number(p?.quantity) || 0;
            const images = getProductImages(p);

            return (
              <div key={p?._id || idx} className="flex items-center gap-3 rounded-xl border border-slate-100 p-2 dark:border-white/5 dark:bg-white/[0.01]">
                <ProductImageCarousel images={images} title={p?.title} size="small" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-1">{p?.title || "Məhsul"}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{quantity} × {price.toFixed(2)} AZN</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-extrabold text-slate-900 dark:text-white">{(price * quantity).toFixed(2)} AZN</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ÇATDIRILMA MƏLUMATLARI */}
      <div>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Çatdırılma Ünvanı</h4>
        {Array.isArray(paymentArray) && paymentArray.length > 0 ? (
          <div className="rounded-xl border border-slate-100 p-3 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.01] space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <InfoItem icon={<FaUser />} label="Ad" value={paymentArray[0]?.name} />
              <InfoItem icon={<FaPhone />} label="Tel" value={paymentArray[0]?.phone} />
              <InfoItem icon={<FaMapMarkerAlt />} label="Şəhər" value={paymentArray[0]?.city} />
              <InfoItem icon={<FaMapMarkerAlt />} label="Ünvan" value={paymentArray[0]?.adress} />
            </div>

            {paymentArray[0]?.poct && (
              <div className="border-t border-slate-200/60 pt-2 dark:border-white/5 flex items-start justify-between gap-2">
                <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{paymentArray[0]?.poct}</p>
                <button
                  type="button"
                  onClick={() => onOpenPoct(paymentArray[0]?.poct)}
                  className="text-[11px] font-bold text-blue-600 dark:text-blue-400 shrink-0"
                >
                  Tam bax
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">Çatdırılma məlumatı yoxdur</p>
        )}
      </div>

      {/* HESABAT */}
      <div className="border-t border-slate-100 dark:border-white/5 pt-3 space-y-1.5 text-xs">
        <div className="flex justify-between text-slate-500 dark:text-slate-400">
          <span>Məhsullar</span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">{(productTotal || 0).toFixed(2)} AZN</span>
        </div>
        <div className="flex justify-between text-slate-500 dark:text-slate-400">
          <span>Çatdırılma</span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">{(poctFee || 0).toFixed(2)} AZN</span>
        </div>
        <div className="flex justify-between text-sm font-extrabold text-slate-900 dark:text-white border-t border-slate-100 dark:border-white/5 pt-2">
          <span>Yekun</span>
          <span>{(total || 0).toFixed(2)} AZN</span>
        </div>
      </div>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div className="min-w-0">
    <span className="flex items-center gap-1 text-[9px] uppercase font-bold text-slate-400">
      {icon} {label}
    </span>
    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{value || "—"}</p>
  </div>
);

export default Confirmed;